import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { candidatesAPI } from '../../services/api';

const VISA_TYPES = [
  { value: 'OPT',         label: 'OPT' },
  { value: 'STEM_OPT',    label: 'STEM OPT' },
  { value: 'CPT',         label: 'CPT' },
  { value: 'H1B',         label: 'H-1B' },
  { value: 'H4_EAD',      label: 'H-4 EAD' },
  { value: 'GC',          label: 'Green Card' },
  { value: 'CITIZENSHIP', label: 'US Citizenship' },
  { value: 'Other',       label: 'Other' },
];

// Auto-formats digits into XXX-XXX-XXXX as the user types
function formatMobile(value) {
  const d = value.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0,3)}-${d.slice(3)}`;
  return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
}

export default function CreateCandidate() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name:       '',
    last_name:        '',
    mobile:           '',
    email:            '',
    visa_type:        '',
    current_location: '',
    referred_by:      '',
    notes:            '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');

  function handleChange(e) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.first_name.trim())  { setError('First name is required.'); return; }
    if (!form.last_name.trim())   { setError('Last name is required.'); return; }
    if (!form.email.trim())       { setError('Email is required.'); return; }
    if (!form.mobile.trim()) { setError('Mobile number is required.'); return; }
    if (form.mobile.replace(/\D/g, '').length !== 10) {
      setError('Mobile number must be exactly 10 digits (e.g. 555-000-0000).');
      return;
    }
    if (!form.visa_type) { setError('Visa type is required.'); return; }
    setSubmitting(true);
    try {
      const res = await candidatesAPI.create(form);
      const id  = res.data?.data?.candidate_id || res.data?.data?.id || res.data?.candidate_id || res.data?.id;
      navigate(`/candidates/${id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create bench entry.');
    } finally {
      setSubmitting(false);
    }
  }

  const inputCls = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/candidates" className="text-slate-400 hover:text-slate-600">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Add New Bench</h1>
          <p className="text-slate-500 text-sm mt-0.5">Create a new bench entry in the hiring pipeline.</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-5">

        {/* Name row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input name="first_name" value={form.first_name} onChange={handleChange}
              required className={inputCls} placeholder="Jane" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input name="last_name" value={form.last_name} onChange={handleChange}
              required className={inputCls} placeholder="Doe" />
          </div>
        </div>

        {/* Email + Mobile */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input name="email" type="email" value={form.email} onChange={handleChange}
              required className={inputCls} placeholder="jane@example.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Mobile <span className="text-red-500">*</span>
            </label>
            <input
              name="mobile"
              value={form.mobile}
              onChange={e => setForm(f => ({ ...f, mobile: formatMobile(e.target.value) }))}
              required
              inputMode="numeric"
              className={inputCls}
              placeholder="555-000-0000"
            />
            <p className="text-xs text-slate-400 mt-1">10 digits — auto-formatted as XXX-XXX-XXXX</p>
          </div>
        </div>

        {/* Visa + Location */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Visa Type <span className="text-red-500">*</span>
            </label>
            <select name="visa_type" value={form.visa_type} onChange={handleChange}
              required className={`${inputCls} bg-white`}>
              <option value="">Select visa type…</option>
              {VISA_TYPES.map((v) => (
                <option key={v.value} value={v.value}>{v.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Current Location</label>
            <input name="current_location" value={form.current_location} onChange={handleChange}
              className={inputCls} placeholder="City, State" />
          </div>
        </div>

        {/* Referred By */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Referred By</label>
          <input name="referred_by" value={form.referred_by} onChange={handleChange}
            className={inputCls} placeholder="Name of person who referred this candidate" />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
          <textarea name="notes" value={form.notes} onChange={handleChange} rows={3}
            className={`${inputCls} resize-none`}
            placeholder="Any initial notes about this candidate…" />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <button type="submit" disabled={submitting}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {submitting ? 'Creating…' : 'Create New Bench'}
          </button>
          <Link to="/candidates" className="text-slate-500 hover:text-slate-700 text-sm font-medium">Cancel</Link>
        </div>
      </form>
    </div>
  );
}
